import { PayloadAction } from '@reduxjs/toolkit';
import { isAxiosError } from 'axios';
import { call, put, takeEvery } from 'redux-saga/effects';
import {
  getModelServersRequest,
  getModelServersSuccess,
  getPromptUsersRequest,
  getPromptUsersSuccess,
  addPromptUserRequest,
  addPromptUserSuccess,
  updatePromptUserRequest,
  updatePromptUserSuccess,
  deletePromptUserRequest,
  deletePromptUserSuccess,
  searchUsersRequest,
  searchUsersSuccess,
  aiPromptUsersInitState,
} from './reducer';
import {
  IGetPromptUsersRequest,
  IModelServersResponse,
  IUsersSearchRequest,
  IUsersSearchResponse,
} from './types';
import { api, responseErrors } from 'services';
import { IGetPromptUsersResponse, IUserRequest } from 'services/api/aiPrompts/types';

function* getModelServersWorker() {
  try {
    const response: { data: IModelServersResponse } = yield call(
      api.aiPromptsServices.getModelServers,
    );
    yield put(getModelServersSuccess(response.data));
  } catch (error) {
    if (isAxiosError(error)) responseErrors(error);
  } finally {
    yield put(aiPromptUsersInitState());
  }
}

function* getPromptUsersWorker(action: PayloadAction<IGetPromptUsersRequest>) {
  try {
    const response: { data: IGetPromptUsersResponse } = yield call(
      api.aiPromptsServices.getPromptUsers,
      action.payload,
    );

    yield put(getPromptUsersSuccess(response.data));
  } catch (error) {
    if (isAxiosError(error)) responseErrors(error);
  } finally {
    yield put(aiPromptUsersInitState());
  }
}

function* addPromptUserWorker(action: PayloadAction<IUserRequest>) {
  try {
    yield call(api.aiPromptsServices.addPromptUser, action.payload);
    yield put(addPromptUserSuccess());

    yield put(
      getPromptUsersRequest({
        bot_id: action.payload.bot_id,
        current_page: 1,
        per_page: 10,
      }),
    );
  } catch (error) {
    if (isAxiosError(error)) responseErrors(error);
  } finally {
    yield put(aiPromptUsersInitState());
  }
}

function* updatePromptUserWorker(action: PayloadAction<{ id: string; payload: IUserRequest }>) {
  try {
    yield call(api.aiPromptsServices.updatePromptUser, action.payload.id, action.payload.payload);
    yield put(updatePromptUserSuccess());
  } catch (error) {
    if (isAxiosError(error)) responseErrors(error);
  } finally {
    yield put(aiPromptUsersInitState());
  }
}

function* deletePromptUserWorker(action: PayloadAction<{ botId: string; userId: string }>) {
  try {
    yield call(api.aiPromptsServices.deletePromptUser, action.payload.userId);
    yield put(deletePromptUserSuccess());

    yield put(
      getPromptUsersRequest({
        bot_id: Number(action.payload.botId),
        current_page: 1,
        per_page: 10,
      }),
    );
  } catch (error) {
    if (isAxiosError(error)) responseErrors(error);
  } finally {
    yield put(aiPromptUsersInitState());
  }
}

function* searchUsersWorker(action: PayloadAction<IUsersSearchRequest>) {
  try {
    const response: { data: IUsersSearchResponse } = yield call(
      api.aiPromptsServices.searchUsers,
      action.payload,
    );
    yield put(searchUsersSuccess(response.data));
  } catch (error) {
    if (isAxiosError(error)) responseErrors(error);
  } finally {
    yield put(aiPromptUsersInitState());
  }
}

export function* aiPromptUsersSaga() {
  yield takeEvery(getModelServersRequest.type, getModelServersWorker);
  yield takeEvery(getPromptUsersRequest.type, getPromptUsersWorker);
  yield takeEvery(addPromptUserRequest.type, addPromptUserWorker);
  yield takeEvery(updatePromptUserRequest.type, updatePromptUserWorker);
  yield takeEvery(deletePromptUserRequest.type, deletePromptUserWorker);
  yield takeEvery(searchUsersRequest.type, searchUsersWorker);
}
